import type { Feedback } from '@wellread/api';

// Feedback is about the part of wellread the reader is on. These are the
// words for each part, read from the address.

export type Place = Feedback['place'];

/** A part of wellread: what the API files it under, and the panel's title for it. */
export interface Part {
  place: Place;
  /** "Feedback on the map". */
  title: string;
}

const part = (place: Place, on: string): Part => ({ place, title: `Feedback on ${on}` });

/** The part of wellread an address is. A book's tabs are their own parts: its map is a map, its notes are notes. */
export function partOf(path: string): Part {
  const book = /^\/books\/[^/]+(\/[^/]+)?/.exec(path);
  if (book) {
    switch (book[1]) {
      case '/read':
        return part('reader', 'the reader');
      case '/graph':
        return part('map', 'the concept map');
      case '/notes':
        return part('notes', 'notes');
      case '/cast':
        return part('book', 'the cast');
      case '/story':
        return part('book', 'the story so far');
      default:
        return part('book', 'a book’s page');
    }
  }
  if (path === '/') return part('home', 'Home');
  if (path === '/library') return part('library', 'the library');
  if (path === '/import') return part('library', 'adding a book');
  if (path === '/review/session') return part('review', 'review');
  if (path === '/map') return part('map', 'the map');
  if (path === '/notes') return part('notes', 'notes');
  if (path === '/tracks/new') return part('track', 'making a track');
  if (/^\/tracks(\/|$)/.test(path)) return part('track', 'tracks');
  if (/^\/explore(\/|$)/.test(path)) return part('explore', 'Explore');
  if (/^\/settings(\/|$)/.test(path)) return part('settings', 'settings');
  return part('other', 'wellread');
}

/** What the reader has said so far: either rating, from 1 to 5, and their words. */
export interface Said {
  useful?: number;
  working?: number;
  wish: string;
  trouble: string;
}

export const NOTHING_SAID: Said = { wish: '', trouble: '' };

/** "What didn't work?" is asked once working is rated 3 or below. */
export const asksTrouble = (s: Said) => s.working !== undefined && s.working <= 3;

/**
 * The feedback as sent: only what was said, with the address and the app.
 * What didn't work goes only while it is asked. Undefined while nothing is
 * said, which the API refuses.
 */
export function feedbackOf(
  s: Said,
  at: { place: Place; path: string; client: string },
): Feedback | undefined {
  const wish = s.wish.trim();
  const trouble = asksTrouble(s) ? s.trouble.trim() : '';
  if (s.useful === undefined && s.working === undefined && !wish && !trouble) return undefined;
  return {
    place: at.place,
    path: at.path.slice(0, 500),
    client: at.client,
    ...(s.useful === undefined ? {} : { useful: s.useful }),
    ...(s.working === undefined ? {} : { working: s.working }),
    ...(wish ? { wish } : {}),
    ...(trouble ? { trouble } : {}),
  };
}
